// S-NRI-1 (بلوک ۵) — the platform's white-label branding config, the SINGLE
// source of brand identity for the web app. EVERY name / number / contact /
// hero copy the landing + shell render comes from this module, never hardcoded
// in JSX, so a second brand/domain can rebrand by overriding ONE module
// (identity here; palette stays OKLCh tokens).
//
// BRAND_KEY selects the active brand (hard rule ۶ سند مادر: rebrand-from-config).
// Today the registry ships `nri` (پژوهشگاه نیرو / وزارت نیرو); the vitest
// white-label check renders the sections with `SAMPLE_ALT_BRAND` to prove
// nothing is baked in.
//
// NOTE(صداقت — سیگنال‌های واقعی، نه تبلیغاتی): آمار نوار اعتبار hero از
// ارقام رسمی وزارت نیرو است (۵ هلدینگ، ~۱۸۰ شرکت، ~۱۷۶هزار کارمند —
// «دامنهٔ پوشش هدف»، نه کاربر فعال فعلی). testimonial جعلی و نشان اینماد
// عمداً وجود ندارد (مشتری حاکمیتی؛ بلوک ۵.۳).

export interface LandingBranding {
  /** هویت */
  name: string; // نام کامل نهاد صاحب برند
  shortName: string; // نام کوتاه برای متن‌های میانی
  /** نام سامانه (محصول) — جدا از نام نهاد */
  platformName: string;
  domain: string;
  /** نهاد بالادست (وزارت نیرو) — فقط لوگو/نام در فوتر لندینگ */
  parentOrg: {
    name: string;
    logo: string | null; // مسیر asset لوکال؛ null = بدون لوگوی بالادست
  };
  /** لوگوهای برند (asset لوکال، بدون CDN) */
  logos: {
    /** نشان اصلی (ناوبار/فوتر/attribution) — webp سبک */
    mark: string;
    /** نسخهٔ کامل/بزرگ (hero یا صفحات درباره) */
    full: string;
  };
  /** آمار نوار اعتبار hero (ارقام لاتین؛ رندر با toFa) */
  stats: {
    faculties: number; // در برند nri: تعداد هلدینگ‌های وزارت نیرو
    programs: number; // در برند nri: تعداد شرکت‌های زیرمجموعه (~)
    instructors: number; // در برند nri: انواع گواهی رسمی (نوع اول/دوم)
    students: number; // در برند nri: کارکنان صنعت — دامنهٔ پوشش هدف
  };
  /** نوار اعتماد زیر hero (بلوک ۵.۳) */
  trustBar: string[];
  /** مبنای قانونی — سیگنال حاکمیتی، در بخش اعتماد رندر می‌شود */
  legalBasis: string;
  /** تماس/اعتماد */
  contact: {
    email: string;
    enamadCode: string | null; // کد اینماد (null = بدون نشان — برند حاکمیتی)
  };
  /** کپی hero + CTA */
  copy: {
    heroTitle: string;
    heroSub: string;
    primaryCta: string;
    secondaryCta: string;
    finalCtaTitle: string;
  };
  /** سوئیچ‌های صداقت — فقط وقتی قابلیت واقعاً ساخته شد روشن شوند. */
  flags: {
    trialBadge: boolean;
    refundBadge: boolean;
  };
}

/** برند پژوهشگاه نیرو / وزارت نیرو — سامانهٔ آموزش ضمن خدمت صنعت آب و برق. */
const NRI_BRAND: LandingBranding = {
  name: "پژوهشگاه نیرو",
  shortName: "پژوهشگاه نیرو",
  platformName: "سامانهٔ هوشمند آموزش ضمن خدمت صنعت آب و برق",
  domain: "nriuniversity.ir",
  parentOrg: {
    name: "وزارت نیرو",
    logo: "/tenants/nri/vezarat-512.webp",
  },
  logos: {
    mark: "/tenants/nri/pajooheshgah-192.webp",
    full: "/tenants/nri/pajooheshgah-920.webp",
  },
  stats: { faculties: 5, programs: 180, instructors: 2, students: 176000 },
  trustBar: [
    "زیر نظر پژوهشگاه نیرو",
    "ویژهٔ کارکنان صنعت آب و برق",
    "گواهی قابل‌راستی‌آزمایی",
  ],
  legalBasis:
    "مبتنی بر نظام آموزش کارمندان دولت (مادهٔ ۵۸ قانون مدیریت خدمات کشوری) و یکپارچه با سامانهٔ مرکزی صلاحیت حرفه‌ای صنعت آب و برق",
  contact: { email: "info@nriuniversity.ir", enamadCode: null },
  copy: {
    heroTitle: "سامانهٔ هوشمند آموزش ضمن خدمت صنعت آب و برق",
    heroSub:
      "پلتفرم ملی آموزش کارکنان وزارت نیرو — دستیارهای هوش مصنوعی متخصص آب و برق، کلاس زندهٔ خودمیزبان، آزمایشگاه مجازی و گواهی ساعت‌محور قابل‌راستی‌آزمایی؛ زیر نظر پژوهشگاه نیرو.",
    primaryCta: "ورود کارکنان",
    secondaryCta: "دستیار هوشمند را همین‌جا امتحان کن",
    finalCtaTitle: "آموزش ضمن خدمت سازمان‌تان را هوشمند کنید",
  },
  flags: { trialBadge: false, refundBadge: false },
};

/** رجیستری برندها — BRAND_KEY (از VITE_BRAND_KEY، پیش‌فرض nri) انتخاب می‌کند. */
const BRAND_REGISTRY: Record<string, LandingBranding> = {
  nri: NRI_BRAND,
};

const BRAND_KEY: string =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BRAND_KEY) ||
  "nri";

export const BRANDING: LandingBranding = BRAND_REGISTRY[BRAND_KEY] ?? NRI_BRAND;

/** برند نمونهٔ دوم — فقط برای تست white-label (S-LP-7)؛ در UI رندر نمی‌شود. */
export const SAMPLE_ALT_BRAND: LandingBranding = {
  name: "آکادمی نمونهٔ آزما",
  shortName: "آزما",
  platformName: "سامانهٔ آموزش آزما",
  domain: "example-academy.test",
  parentOrg: { name: "نهاد نمونهٔ بالادست", logo: null },
  logos: { mark: "/tenants/nri/pajooheshgah-192.webp", full: "/tenants/nri/pajooheshgah-920.webp" },
  stats: { faculties: 3, programs: 42, instructors: 17, students: 950 },
  trustBar: ["نشان اعتماد اول", "نشان اعتماد دوم"],
  legalBasis: "مبنای قانونی نمونه برای تست white-label.",
  contact: { email: "hello@example-academy.test", enamadCode: "TEST-123" },
  copy: {
    heroTitle: "یادگیری با همراه هوشمند",
    heroSub: "زیرنویس تستی برند دوم.",
    primaryCta: "شروع رایگان",
    secondaryCta: "دمو را ببین",
    finalCtaTitle: "از امروز شروع کن",
  },
  flags: { trialBadge: true, refundBadge: true },
};
